import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, Button, Dialog, type BadgeTone } from '@artosera/ui';
import { formaterDate } from '../../../../lib/format.js';
import { formaterJour } from '../../../../lib/ouvertures.js';
import { ETAPES_BASCULE, statutEtape, type StatutEtapeBascule } from '../../../../lib/recette.js';
import type { DossierOuverture, EtapeBascule, RecetteOuverture } from '../../../../types.js';
import pageStyles from '../../../../styles/page.module.css';
import styles from './Recette.module.css';

const TON_STATUT: Record<StatutEtapeBascule, BadgeTone> = {
  faite: 'available',
  erreur: 'sold',
  en_cours: 'reserved',
  attente: 'neutral',
};

/** Ce que la bascule fait avant la mise en service, dans l'ordre (spec § 8.12). */
const PAS_CONFIRMES: readonly EtapeBascule[] = [
  'controle',
  'scellement',
  'mandat',
  'purge',
  'invitations',
];

/**
 * La bascule (spec § 8.12) : la date prévue, le bouton « Basculer » avec sa
 * confirmation qui énumère ce qui va se passer et dit que c'est irréversible,
 * puis les étapes faites ou non et l'erreur éventuelle, avec « Reprendre la
 * bascule ». Seule la direction de Venio bascule, second facteur récent.
 */
export function BasculeBloc({
  dossier,
  recette,
  peutBasculer,
  enCours,
  surBascule,
  boiteMasquee,
}: {
  dossier: DossierOuverture;
  recette: RecetteOuverture;
  peutBasculer: boolean;
  enCours: boolean;
  /** Rend `true` si la bascule est allée au bout. */
  surBascule: () => Promise<boolean>;
  boiteMasquee: boolean;
}): JSX.Element {
  const { t } = useTranslation('console');
  const cle = 'ouvertures.fiche.recette.bascule';
  const [boite, setBoite] = useState(false);
  const { bascule } = recette;
  const planifiee = recette.etat === 'bascule_planifiee';
  const enService = recette.etat === 'en_service';
  const reprise = bascule.erreur !== null || bascule.etapes.length > 0;
  const montreEtapes = planifiee || enService || bascule.etapes.length > 0;
  const enTrainDeBasculer = enCours || bascule.enCours;

  const lancer = async (): Promise<void> => {
    setBoite(false);
    await surBascule();
  };

  return (
    <section className={pageStyles.panneau} aria-labelledby="rec-bascule">
      <div className={pageStyles.sectionTitre}>
        <h2 id="rec-bascule">{t(`${cle}.titre`)}</h2>
        {enService ? <Badge tone="available">{t(`${cle}.enService`)}</Badge> : null}
      </div>

      <dl className={styles.faits}>
        <dt>{t(`${cle}.prevue`)}</dt>
        <dd>
          {bascule.prevueLe ? formaterJour(bascule.prevueLe) : t(`${cle}.nonDatee`)}
          {!enService && recette.etat !== 'abandonne' ? (
            <>
              {' '}
              <Link to={`/ouvertures/${dossier.id}/synthese`} className="lien-cible">
                {t(`${cle}.modifier`)}
              </Link>
            </>
          ) : null}
        </dd>
        {bascule.effectueeLe ? (
          <>
            <dt>{t(`${cle}.effectuee`)}</dt>
            <dd>{formaterDate(bascule.effectueeLe, '—')}</dd>
          </>
        ) : null}
      </dl>

      {bascule.erreur ? (
        <div className={`${styles.erreur} ${styles.espaceHaut}`} role="alert">
          <strong>
            {t(`${cle}.erreurTitre`, { etape: t(`${cle}.etape.${bascule.erreur.etape}`) })}
          </strong>{' '}
          {bascule.erreur.message}
          <span className={styles.etapeNote}>{t(`${cle}.erreurAide`)}</span>
        </div>
      ) : null}

      {montreEtapes ? (
        <>
          <h3 className={styles.titreBloc}>{t(`${cle}.etapes`)}</h3>
          <ol className={styles.etapes}>
            {ETAPES_BASCULE.map((etape, rang) => {
              const statut = statutEtape(bascule, etape);
              const faite = bascule.etapes.find((candidate) => candidate.cle === etape);
              return (
                <li key={etape} className={styles.etape} data-statut={statut} data-etape={etape}>
                  <span className={styles.etapeNumero} aria-hidden="true">
                    {rang + 1}
                  </span>
                  <span>
                    {t(`${cle}.etape.${etape}`)}
                    {faite ? (
                      <span className={styles.etapeNote}>
                        {[formaterDate(faite.le, ''), faite.note]
                          .filter((morceau) => morceau !== '')
                          .join(' · ')}
                      </span>
                    ) : null}
                  </span>
                  <Badge tone={TON_STATUT[statut]}>{t(`${cle}.etat.${statut}`)}</Badge>
                </li>
              );
            })}
          </ol>
        </>
      ) : null}

      {planifiee && peutBasculer ? (
        <div className={styles.boutons}>
          <Button
            variant="danger"
            disabled={enTrainDeBasculer}
            loading={enTrainDeBasculer}
            onClick={() => setBoite(true)}
          >
            {t(`${cle}.${reprise ? 'reprendre' : 'action'}`)}
          </Button>
        </div>
      ) : null}
      {planifiee && !peutBasculer ? (
        <p className={`${styles.discret} ${styles.titreBloc}`}>{t(`${cle}.lectureSeule`)}</p>
      ) : null}
      {!planifiee && !enService && recette.etat !== 'abandonne' ? (
        <p className={`${styles.note} ${styles.titreBloc}`}>{t(`${cle}.avant`)}</p>
      ) : null}

      <Dialog
        open={boite && !boiteMasquee}
        onOpenChange={setBoite}
        title={t(`${cle}.confirmation.${reprise ? 'titreReprise' : 'titre'}`, {
          nom: dossier.identite.nomCommercial,
        })}
        description={t(`${cle}.confirmation.${reprise ? 'texteReprise' : 'texte'}`)}
        closeLabel={t(`${cle}.confirmation.fermer`)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setBoite(false)}>
              {t(`${cle}.confirmation.annuler`)}
            </Button>
            <Button variant="danger" onClick={() => void lancer()}>
              {t(`${cle}.${reprise ? 'reprendre' : 'confirmation.confirmer'}`)}
            </Button>
          </>
        }
      >
        <ol className={styles.pas}>
          {PAS_CONFIRMES.map((etape) => {
            const faite = statutEtape(bascule, etape) === 'faite';
            return (
              <li key={etape} data-faite={faite}>
                {t(`${cle}.confirmation.pas.${etape}`)}
                {faite ? (
                  <>
                    {' '}
                    <span className={styles.pasFait}>({t(`${cle}.confirmation.faite`)})</span>
                  </>
                ) : null}
              </li>
            );
          })}
        </ol>
        <p className={styles.texte}>{t(`${cle}.confirmation.final`)}</p>
        <p className={styles.irreversible}>{t(`${cle}.confirmation.irreversible`)}</p>
      </Dialog>
    </section>
  );
}
